// Android Google OAuth bridge.
//
// Consent runs in a system-owned Custom Tab, never in the embedded WebView
// (Google rejects embedded user agents). The app keeps a PKCE verifier in
// Capacitor Preferences while the HTTPS callback returns a bound, encrypted
// handoff code through com.poneva.chat://oauth/google.

import { isNativeShell, nativePlatform, nativePlugin } from './shell';

const CALLBACK_PROTOCOL = 'com.poneva.chat:';
const CALLBACK_HOST = 'oauth';
const CALLBACK_PATH = '/google';
const VERIFIER_KEY = 'poneva.nativeGoogleOAuthVerifier';
const CONSUMED_LAUNCH_URL_KEY = 'poneva.nativeGoogleOAuthLaunchUrl';
const PKCE_VALUE = /^[A-Za-z0-9_-]{43,128}$/;

interface PreferencesPlugin {
  get(opts: { key: string }): Promise<{ value: string | null }>;
  set(opts: { key: string; value: string }): Promise<void>;
  remove(opts: { key: string }): Promise<void>;
}

interface BrowserPlugin {
  open(opts: { url: string }): Promise<void>;
  close(): Promise<void>;
}

interface PluginListenerHandle {
  remove(): Promise<void>;
}

interface AppPlugin {
  addListener(
    eventName: 'appUrlOpen',
    listener: (event: { url: string }) => void,
  ): Promise<PluginListenerHandle>;
  getLaunchUrl(): Promise<{ url?: string }>;
}

export type NativeGoogleCallback = { code: string; verifier: string } | { error: string };

export const isAndroidNativeShell = (): boolean => nativePlatform() === 'android';

/** Preserve web providers, enable Android Google, and retain the existing iOS gate. */
export function oauthProvidersForCurrentPlatform(providers: string[]): string[] {
  if (!isNativeShell()) return providers;
  const platform = nativePlatform();
  return platform === 'android' ? providers : [];
}

const preferences = () => nativePlugin<PreferencesPlugin>('Preferences');
const browser = () => nativePlugin<BrowserPlugin>('Browser');
const app = () => nativePlugin<AppPlugin>('App');

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function persistVerifier(verifier: string): Promise<void> {
  const prefs = preferences();
  if (prefs) {
    await prefs.set({ key: VERIFIER_KEY, value: verifier });
    return;
  }
  sessionStorage.setItem(VERIFIER_KEY, verifier);
}

async function readVerifier(): Promise<string | null> {
  const prefs = preferences();
  return prefs
    ? (await prefs.get({ key: VERIFIER_KEY })).value
    : sessionStorage.getItem(VERIFIER_KEY);
}

async function consumeLaunchUrl(url: string): Promise<boolean> {
  const prefs = preferences();
  if (prefs) {
    const previous = (await prefs.get({ key: CONSUMED_LAUNCH_URL_KEY })).value;
    if (previous === url) return false;
    await prefs.set({ key: CONSUMED_LAUNCH_URL_KEY, value: url });
    return true;
  }
  if (sessionStorage.getItem(CONSUMED_LAUNCH_URL_KEY) === url) return false;
  sessionStorage.setItem(CONSUMED_LAUNCH_URL_KEY, url);
  return true;
}

export async function clearNativeGoogleVerifier(): Promise<void> {
  const prefs = preferences();
  if (prefs) {
    await prefs.remove({ key: VERIFIER_KEY });
    return;
  }
  sessionStorage.removeItem(VERIFIER_KEY);
}

/** Create and persist the app-side PKCE proof before requesting a start URL. */
export async function prepareNativeGoogleSignIn(): Promise<{
  verifier: string;
  challenge: string;
}> {
  if (!isAndroidNativeShell()) throw new Error('native Google sign-in is Android-only');
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = base64url(new Uint8Array(digest));
  await persistVerifier(verifier);
  return { verifier, challenge };
}

export async function openNativeGoogleSignIn(url: string): Promise<void> {
  const plugin = browser();
  if (!plugin) throw new Error('Capacitor Browser plugin is unavailable');
  await plugin.open({ url });
}

async function parseCallback(value: string): Promise<NativeGoogleCallback | null> {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (
    url.protocol !== CALLBACK_PROTOCOL ||
    url.host !== CALLBACK_HOST ||
    url.pathname !== CALLBACK_PATH
  ) {
    return null;
  }
  await browser()
    ?.close()
    .catch(() => {});
  const error = url.searchParams.get('error');
  if (error) {
    await clearNativeGoogleVerifier();
    return { error };
  }
  const code = url.searchParams.get('code');
  const verifier = await readVerifier();
  if (!code || !verifier || !PKCE_VALUE.test(verifier)) {
    await clearNativeGoogleVerifier();
    return { error: 'expired' };
  }
  return { code, verifier };
}

/** Handle warm appUrlOpen events; cleanup removes the listener on unmount. */
export async function listenForNativeGoogleCallback(
  onCallback: (callback: NativeGoogleCallback) => void | Promise<void>,
): Promise<() => Promise<void>> {
  if (!isAndroidNativeShell()) return async () => {};
  const plugin = app();
  if (!plugin) throw new Error('Capacitor App plugin is unavailable');
  let active = true;
  const deliver = async (url: string) => {
    const callback = await parseCallback(url);
    if (active && callback) await onCallback(callback);
  };
  const handle = await plugin.addListener('appUrlOpen', ({ url }) => void deliver(url));
  return async () => {
    active = false;
    await handle.remove();
  };
}

/** Consume an OAuth URL that cold-launched the Android process, if present. */
export async function getNativeGoogleLaunchCallback(): Promise<NativeGoogleCallback | null> {
  if (!isAndroidNativeShell()) return null;
  const plugin = app();
  if (!plugin) throw new Error('Capacitor App plugin is unavailable');
  const launch = await plugin.getLaunchUrl();
  if (!launch.url || !(await consumeLaunchUrl(launch.url))) return null;
  return parseCallback(launch.url);
}
